import { z } from "zod";
import { CountryCode } from "../common/country.js";
import { Id } from "../common/ids.js";
import { LocalizedText } from "../common/localized-text.js";
import { CurrencyCode, Money } from "../common/money.js";
import { PageQuery, page } from "../common/pagination.js";
import { Quantity } from "../common/quantity.js";
import { IsoDate, Timestamp } from "../common/time.js";
import { defineRoute } from "../http/route.js";
/**
 * Sellers' commercial invoices and credit notes (INV-2, card 093; the owner's rules of 2026-10-04). The seller issues
 * them on a paid order for lines and quantities not yet invoiced, in its own number series, with a tax category per
 * line; an issued invoice never changes, and a credit note corrects it. The order's prices are what the buyer pays, so
 * a standard-rated line carries its GST within its amount. Each is held in PINT SG's business terms, validated when
 * issued, and both parties download its PDF.
 */
export const TaxCategory = z.enum(["SR", "ZR", "OS", "NG"]).meta({
    id: "TaxCategory",
    description: "SR standard-rated, ZR zero-rated, OS out of scope (GST-registered sellers); NG not registered for GST",
});
export const InvoiceSettings = z
    .object({
    prefix: z.string().nullable().describe("Null until the seller sets it"),
    gstRegistrationNumber: z.string().nullable(),
    prefixLocked: z.boolean().describe("Fixed once the first invoice is issued"),
    version: z.number().int().positive().nullable(),
})
    .meta({ id: "InvoiceSettings" });
const Party = z.object({
    name: z.string(),
    legalId: z.string(),
    taxId: z.string().nullable(),
    country: CountryCode,
    address: z
        .object({ lines: z.array(z.string()), city: z.string().nullable(), postalCode: z.string().nullable() })
        .nullable(),
});
export const InvoiceLineView = z.object({
    id: Id,
    lineNo: z.number().int().positive(),
    orderLineId: Id.nullable(),
    chargeIndex: z.number().int().nonnegative().nullable(),
    creditedLineId: Id.nullable(),
    adjustment: z.boolean().describe("A credit by amount, which leaves the quantity invoiced"),
    name: z.string(),
    quantity: Quantity,
    unitCode: z.string(),
    gross: Money.describe("What the buyer pays for the line, GST included"),
    net: Money,
    tax: Money,
    taxCategory: TaxCategory,
    taxRateBps: z.number().int(),
});
export const InvoiceView = z
    .object({
    id: Id,
    type: z.enum(["invoice", "credit_note"]),
    number: z.string(),
    orderId: Id,
    orderNumber: z.string(),
    side: z.enum(["buyer", "seller"]).describe("The caller's side"),
    creditNoteOf: z.object({ id: Id, number: z.string() }).nullable(),
    creditNotes: z.array(z.object({ id: Id, number: z.string() })).describe("Credit notes issued against it"),
    issueDate: IsoDate,
    dueDate: IsoDate.nullable(),
    net: Money,
    tax: Money,
    gross: Money,
    paid: Money.describe("What the order's payments covered when it was issued"),
    payable: Money,
    gstRateBps: z.number().int(),
    taxInSgd: Money.nullable().describe("GST in SGD when charged in another currency"),
    taxFx: z
        .object({
        base: CurrencyCode,
        quote: CurrencyCode,
        rate: z.string(),
        source: z.string(),
        capturedAt: Timestamp,
    })
        .nullable(),
    seller: Party,
    buyer: Party,
    note: z.string().nullable(),
    lines: z.array(InvoiceLineView),
    fileId: Id.nullable().describe("The PDF, moments after issue"),
    createdAt: Timestamp,
})
    .meta({ id: "InvoiceView" });
export const InvoiceSummary = InvoiceView.omit({ lines: true, seller: true, buyer: true, taxFx: true })
    .extend({ counterpartyName: z.string() })
    .meta({ id: "InvoiceSummary" });
/** What is left to invoice on an order, for the seller's form. */
export const Invoicing = z
    .object({
    open: z.boolean().describe("Whether the order may be invoiced now: paid, and not cancelled"),
    currency: CurrencyCode,
    gstRateBps: z.number().int(),
    settings: InvoiceSettings,
    lines: z.array(z.object({
        orderLineId: Id,
        name: LocalizedText,
        skuCode: z.string().nullable(),
        unit: z.string(),
        suggestedUnitCode: z.string(),
        quantity: Quantity,
        unitPrice: Money,
        total: Money,
        invoicedQuantity: Quantity,
        invoicedAmount: Money,
    })),
    charges: z.array(z.object({
        chargeIndex: z.number().int().nonnegative(),
        kind: z.enum(["freight", "insurance", "other"]),
        amount: Money,
        invoiced: z.boolean(),
    })),
    paidOnOrder: Money,
    invoicedBefore: Money.describe("Invoices issued, gross"),
    creditedBefore: Money.describe("Credit notes issued, gross"),
})
    .meta({ id: "Invoicing" });
const Seller = { surface: "tenant", auth: "user", permission: "invoice:issue" };
const Reader = { surface: "tenant", auth: "user", permission: "order:read" };
export const getInvoiceSettings = defineRoute({
    ...Seller,
    id: "trade.invoice-settings.get",
    method: "GET",
    path: "/v1/invoice-settings",
    marks: ["tenant"],
    summary: "The seller's invoice prefix and GST registration number",
    prd: ["INV-2"],
    responses: { 200: { description: "Settings", schema: InvoiceSettings } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED"],
});
export const putInvoiceSettings = defineRoute({
    ...Seller,
    id: "trade.invoice-settings.put",
    method: "PUT",
    path: "/v1/invoice-settings",
    marks: ["tenant"],
    summary: "Sets the prefix, which is fixed once an invoice is issued, and the GST registration number (null when not registered)",
    prd: ["INV-2"],
    request: {
        body: z.object({
            prefix: z.string().regex(/^[A-Z0-9]{2,10}$/),
            gstRegistrationNumber: z
                .string()
                .trim()
                .toUpperCase()
                .regex(/^[A-Z0-9-]{8,15}$/)
                .nullable(),
        }),
    },
    responses: { 200: { description: "Settings", schema: InvoiceSettings } },
    errors: [
        "AUTH_REQUIRED",
        "TENANT_REQUIRED",
        "PERMISSION_DENIED",
        "VALIDATION_FAILED",
        "INVALID_TRANSITION",
    ],
});
export const getInvoicing = defineRoute({
    ...Seller,
    id: "trade.invoicing.get",
    method: "GET",
    path: "/v1/orders/{orderId}/invoicing",
    marks: ["tenant"],
    summary: "What is left to invoice on the order, with the seller's settings and the GST rate",
    prd: ["INV-2"],
    request: { params: z.object({ orderId: Id }) },
    responses: { 200: { description: "What is left", schema: Invoicing } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND"],
});
export const issueInvoice = defineRoute({
    ...Seller,
    id: "trade.invoices.issue",
    method: "POST",
    path: "/v1/orders/{orderId}/commercial-invoices",
    marks: ["tenant", "idem"],
    summary: "Issues an invoice for order lines and quantities not yet invoiced and charges not yet invoiced, numbered in the seller's series and validated against PINT SG",
    prd: ["INV-2"],
    request: {
        params: z.object({ orderId: Id }),
        body: z.object({
            lines: z
                .array(z.object({
                orderLineId: Id.optional(),
                chargeIndex: z.number().int().nonnegative().optional(),
                quantity: Quantity.optional().describe("For an order line; a charge is invoiced whole"),
                unitCode: z.string().min(1).max(4),
                taxCategory: TaxCategory,
            }))
                .min(1)
                .max(60),
            dueDate: IsoDate.optional(),
            note: z.string().trim().min(1).max(500).optional(),
        }),
    },
    responses: { 201: { description: "The invoice", schema: InvoiceView } },
    errors: [
        "AUTH_REQUIRED",
        "TENANT_REQUIRED",
        "PERMISSION_DENIED",
        "NOT_FOUND",
        "VALIDATION_FAILED",
        "INVALID_TRANSITION",
        "INVOICE_SETTINGS_REQUIRED",
        "FX_UNAVAILABLE",
    ],
});
export const issueCreditNote = defineRoute({
    ...Seller,
    id: "trade.invoices.credit",
    method: "POST",
    path: "/v1/invoices/{invoiceId}/credit-notes",
    marks: ["tenant", "idem"],
    summary: "Issues a credit note against an invoice: part of a line's quantity, which may then be invoiced again, or an amount off a line",
    prd: ["INV-2"],
    request: {
        params: z.object({ invoiceId: Id }),
        body: z.object({
            lines: z
                .array(z.object({
                invoiceLineId: Id,
                quantity: Quantity.optional(),
                amount: Money.optional().describe("A price adjustment, GST included"),
            }))
                .min(1)
                .max(60),
            reason: z.string().trim().min(3).max(500),
        }),
    },
    responses: { 201: { description: "The credit note", schema: InvoiceView } },
    errors: [
        "AUTH_REQUIRED",
        "TENANT_REQUIRED",
        "PERMISSION_DENIED",
        "NOT_FOUND",
        "VALIDATION_FAILED",
        "INVALID_TRANSITION",
        "INVOICE_SETTINGS_REQUIRED",
    ],
});
export const listInvoices = defineRoute({
    ...Reader,
    id: "trade.invoices.list",
    method: "GET",
    path: "/v1/invoices",
    marks: ["tenant"],
    summary: "Invoices and credit notes the enterprise issued or received, newest first, optionally of one order",
    prd: ["INV-2"],
    request: {
        query: PageQuery.extend({ orderId: Id.optional(), side: z.enum(["buyer", "seller"]).optional() }),
    },
    responses: { 200: { description: "Invoices", schema: page(InvoiceSummary) } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED", "VALIDATION_FAILED"],
});
export const getInvoice = defineRoute({
    ...Reader,
    id: "trade.invoices.get",
    method: "GET",
    path: "/v1/invoices/{invoiceId}",
    marks: ["tenant"],
    summary: "An invoice or credit note with its lines, for either party",
    prd: ["INV-2"],
    request: { params: z.object({ invoiceId: Id }) },
    responses: { 200: { description: "The invoice", schema: InvoiceView } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND"],
});
export const getInvoicePeppol = defineRoute({
    ...Reader,
    id: "trade.invoices.peppol",
    method: "GET",
    path: "/v1/invoices/{invoiceId}/peppol",
    marks: ["tenant"],
    summary: "The invoice's PINT SG business terms as stored, amounts as decimals (docs/research/peppol-pint-sg.md)",
    prd: ["INV-2"],
    request: { params: z.object({ invoiceId: Id }) },
    responses: { 200: { description: "The PINT SG document", schema: z.record(z.string(), z.unknown()) } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND"],
});
export const invoiceRoutes = [
    getInvoiceSettings,
    putInvoiceSettings,
    getInvoicing,
    issueInvoice,
    issueCreditNote,
    listInvoices,
    getInvoice,
    getInvoicePeppol,
];
//# sourceMappingURL=invoice.routes.js.map